"use client"

import type React from "react"

import { FiClock, FiPlus } from "react-icons/fi"
import { SchedulePanel } from "./schedulePanel"
import { cn } from "@/utils/classNames"
import { Button } from "@/components/ui/Button"

// Modified Schedule type to match DashboardPage data
type Schedule = {
  ID: number
  DeviceID: string
  Index: number
  Name: string
  PumpSpeed: number
  AuxType: number
  AuxSlot: number
  IsScheduleOn: boolean
  StartTime: number
  EndTime: number
  WeekDay?: number
  weekdays_array?: boolean[]
}

// Modified DeviceCardProps to match AuxDevice data
type DeviceCardProps = {
  device: {
    ID: number
    DeviceID: string
    Name: string
    Slot: number
    Type: number
    IsTrump: boolean
    IsOn: boolean
    icon?: React.ReactNode
    schedules: Schedule[]
  }
  isSelected: boolean
  onClick: () => void
}

export function DeviceCard({ device, isSelected, onClick }: DeviceCardProps) {
  const statusColor = {
    Active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    Inactive: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400",
    Disabled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  }

  const hasSchedules = device.schedules.length > 0
  
  // Determine device status based on IsOn property
  const deviceStatus = device.IsOn ? "Active" : "Inactive"

  return (
    <div className="relative isolate">
      <div
        className={cn(
          "rounded-lg border transition-all duration-200 overflow-hidden flex flex-col dark:border-gray-700",
          isSelected
            ? "border-cyan-500 dark:border-cyan-400 shadow-md"
            : "border-gray-200 hover:border-cyan-300 dark:hover:border-cyan-400",
        )}
        style={{ height: isSelected ? 'auto' : '90px' }}
      >
        <div 
          className="p-4 cursor-pointer flex items-center justify-between h-[90px] bg-white dark:bg-gray-800/60" 
          onClick={onClick}
        >
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-cyan-50 dark:bg-cyan-900/30 p-2.5 w-11 h-11 flex items-center justify-center text-cyan-500 dark:text-cyan-300">
              {device.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">{device.Name}</h3>
              <div className={cn("text-xs px-2 py-0.5 rounded-full inline-block mt-1", statusColor[deviceStatus])}>
                {deviceStatus}
              </div>
            </div>
          </div>

          {hasSchedules && (
            <div className="flex items-center text-gray-500 dark:text-gray-400 ml-2">
              <FiClock className="h-4 w-4 mr-1" />
              <span className="text-xs whitespace-nowrap">
                {device.schedules.length} {device.schedules.length > 1 ? "schedules" : "schedule"}
              </span>
            </div>
          )}
          
          {!hasSchedules && (
            <div className="flex items-center text-gray-400 dark:text-gray-500 ml-2">
              <FiClock className="h-4 w-4 mr-1" />
              <span className="text-xs whitespace-nowrap">
                No schedules
              </span>
            </div>
          )}
        </div>

        {isSelected && (
          <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            {hasSchedules ? (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">{device.Name} Schedules</h4>
                </div>

                <div className="space-y-4">
                  {device.schedules.map((schedule) => (
                    <SchedulePanel 
                      key={schedule.ID} 
                      schedule={{
                        id: schedule.ID.toString(),
                        name: schedule.Name,
                        time: `${formatMinutesToTime(schedule.StartTime)} - ${formatMinutesToTime(schedule.EndTime)}`,
                        days: getWeekdayArray(schedule.weekdays_array),
                        enabled: schedule.IsScheduleOn
                      }} 
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-6 flex flex-col items-center justify-center text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No schedules configured</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Helper function to format minutes to HH:MM time
function formatMinutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// Helper function to convert Weekdays number to array of day objects
function getWeekdayArray(weekdaysArray?: boolean[]): Array<{name: string, active: boolean}> {
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  if (weekdaysArray) {
    return dayNames.map((name, index) => ({
      name,
      active: weekdaysArray[index] || false
    }));
  }
  
  // 创建一个默认的星期数组（全部不激活）
  return dayNames.map(name => ({ name, active: false }));
}
